import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Title, Text, Stack, Group, Loader, rem, SimpleGrid, Badge, Divider, Tooltip, Box, ActionIcon, Accordion, Button } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import EChartsPie from '../components/echarts/EChartsPie';
import { fetchSummary, fetchDevicesDirectory } from '../services/dataService';
import { useMediaQuery } from '@mantine/hooks';
import type { DataQuery, DeviceSummaryItem, SummaryResult, DevicesDirectory, DeviceRef } from '../types';
import { Clock, Battery, Thermometer, Droplets, Gauge, RadioTower } from 'lucide-react';

// Local storage key for thresholds
const THRESHOLDS_STORAGE_KEY = 'tt.summary.thresholds';

type SummaryThresholds = {
  batteryLow: number; // %
  inactiveDays: number; // days without communication considered stale
  batteryMedium: number; // % medium threshold above low
  inactiveDaysWarn: number; // days without communication considered warning
};

const defaultThresholds: SummaryThresholds = {
  batteryLow: 20,
  inactiveDays: 7,
  batteryMedium: 40,
  inactiveDaysWarn: 3,
};

const STATUS = {
  // Softer pastel-like colors for charts
  green: '#86efac',  // pastel green
  yellow: '#fde68a', // pastel yellow
  red: '#fca5a5',    // pastel red
};

// Slightly stronger (less translucent-looking) colors for charts
const CHART = {
  green: '#4ade80', // emerald-400
  yellow: '#facc15', // yellow-400
  red: '#f87171',    // red-400
};

const SummaryPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 48em)');
  const navigate = useNavigate();

  // Backend should default to last 24h; no explicit from/to here

  // Metrics filter removed

  const [query, _setQuery] = useState<DataQuery>({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<DeviceSummaryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [metricsList, setMetricsList] = useState<string[]>([]);
  const [directory, setDirectory] = useState<DevicesDirectory | null>(null);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [__hoverSensorId, __setHoverSensorId] = useState<string | null>(null);

  // Load thresholds from localStorage (same as SettingsPage)
  const [thresholds, setThresholds] = useState<SummaryThresholds>(() => {
    try {
      const raw = localStorage.getItem(THRESHOLDS_STORAGE_KEY);
      return raw ? { ...defaultThresholds, ...JSON.parse(raw) } : defaultThresholds;
    } catch {
      return defaultThresholds;
    }
  });

  // Update thresholds when localStorage changes (sync with SettingsPage)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === THRESHOLDS_STORAGE_KEY && e.newValue) {
        try {
          const newThresholds = { ...defaultThresholds, ...JSON.parse(e.newValue) };
          setThresholds(newThresholds);
        } catch {
          // Ignore invalid JSON
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Calculate thresholds in milliseconds for comparison
  const WARNING_MS = thresholds.inactiveDaysWarn * 24 * 60 * 60 * 1000;
  const INACTIVE_MS = thresholds.inactiveDays * 24 * 60 * 60 * 1000;

  // Preset/time filter removed

  // Pagination removed

  // Device search preload removed

  // Preset effect removed

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.allSettled([
      fetchSummary({ gatewayId: query.gatewayId, sensorId: query.sensorId }),
      fetchDevicesDirectory(),
    ])
      .then(([sumRes, dirRes]) => {
        if (cancelled) return;
        if (sumRes.status === 'fulfilled') {
          const res = sumRes.value as SummaryResult;
          setItems(res.items);
          setTotal(res.total);
          setMetricsList(res.metrics || []);
        }
        if (dirRes.status === 'fulfilled') {
          setDirectory(dirRes.value);
        }
      })
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [query.gatewayId, query.sensorId]);

  // Aggregation per device for table
  // Map-like view of items keyed by id for convenience
  const byDevice = useMemo(() => {
    const map = new Map<string, DeviceSummaryItem>();
    items.forEach((it) => map.set(it.id, it));
    
    // If backend didn't provide gateways, aggregate them from sensors and directory
    if (directory && directory.gateways) {
      const __sensorsById = new Map((directory.sensors || []).map((s) => [s.id.toLowerCase(), s]));
      
      for (const gateway of directory.gateways) {
        if (!map.has(gateway.id.toLowerCase())) {
          // Aggregate gateway data from its sensors
          const sensorIds = directory.mapping?.[gateway.id] || [];
          const sensorItems = sensorIds
            .map((sid) => items.find((item) => item.id.toLowerCase() === sid.toLowerCase()))
            .filter(Boolean) as DeviceSummaryItem[];
          
          if (sensorItems.length > 0) {
            // Compute gateway metrics from sensors
            const metricsAgg: Record<string, { nums: number[]; unit?: string }> = {};
            let lastTs: string | undefined;
            
            sensorItems.forEach((sensor) => {
              if (!lastTs || (sensor.lastTs && sensor.lastTs > lastTs)) {
                lastTs = sensor.lastTs;
              }
              
              Object.entries(sensor.metrics || {}).forEach(([metricKey, metricData]) => {
                if (!metricsAgg[metricKey]) {
                  metricsAgg[metricKey] = { nums: [], unit: metricData.unit };
                }
                metricsAgg[metricKey].nums.push(metricData.avg);
              });
            });
            
            // Compute averages
            const metricsOut: Record<string, { avg: number; max: number; unit: string }> = {};
            Object.entries(metricsAgg).forEach(([metricKey, data]) => {
              if (data.nums.length > 0) {
                const avg = data.nums.reduce((s, n) => s + n, 0) / data.nums.length;
                const mx = Math.max(...data.nums);
                metricsOut[metricKey] = {
                  avg: Math.round(avg * 100) / 100,
                  max: Math.round(mx * 100) / 100,
                  unit: data.unit || '',
                };
              }
            });
            
            // Compute average battery from sensors
            const batteries = sensorItems
              .map((s) => s.battery)
              .filter((b) => typeof b === 'number') as number[];
            const battery = batteries.length > 0
              ? Math.round(batteries.reduce((s, n) => s + n, 0) / batteries.length)
              : undefined;
            
            map.set(gateway.id.toLowerCase(), {
              id: gateway.id,
              name: gateway.name,
              kind: 'gateway',
              lastTs,
              battery,
              metrics: metricsOut,
            });
          }
        }
      }
    }
    
    return map;
  }, [items, directory]);


  // Last communication buckets - filter by selection if available
  const lastCommPie = useMemo(() => {
    const now = Date.now();
    let green = 0, yellow = 0, red = 0;
    const itemsToCount = selectedAssetIds.size > 0
      ? items.filter((d) => selectedAssetIds.has(d.id.toLowerCase()))
      : items;
    
    itemsToCount.forEach((d) => {
      if (!d.lastTs) { red++; return; }
      const ts = Date.parse(d.lastTs);
      if (!Number.isFinite(ts)) { red++; return; }
      const diffMs = now - ts;
      
      // Now = below warning threshold (green)
      if (diffMs <= WARNING_MS) green++;
      // Recently = between warning and inactive thresholds (yellow)  
      else if (diffMs <= INACTIVE_MS) yellow++;
      // Offline = above inactive threshold (red)
      else red++;
    });
    return [
      { name: 'Now', value: green, color: CHART.green },
      { name: 'Recenty', value: yellow, color: CHART.yellow },
      { name: 'Offline', value: red, color: CHART.red },
    ];
  }, [items, selectedAssetIds, WARNING_MS, INACTIVE_MS]);

  // Battery buckets - filter by selection if available
  const batteryPie = useMemo(() => {
    let green = 0, yellow = 0, red = 0;
    const itemsToCount = selectedAssetIds.size > 0
      ? items.filter((d) => selectedAssetIds.has(d.id.toLowerCase()))
      : items;
    
    itemsToCount.forEach((d) => {
      const b = typeof d.battery === 'number' ? d.battery : null;
      if (b == null) { red++; return; }
      if (b >= thresholds.batteryMedium) green++;
      else if (b >= thresholds.batteryLow) yellow++;
      else red++;
    });
    return [
      { name: 'High', value: green, color: CHART.green },
      { name: 'Medium', value: yellow, color: CHART.yellow },
      { name: 'Low', value: red, color: CHART.red },
    ];
  }, [items, selectedAssetIds, thresholds]);

  // Helpers to compute colors for values
  const getCommColor = (ts?: string | null) => {
    if (!ts) return STATUS.red;
    const parsed = Date.parse(ts);
    if (!Number.isFinite(parsed)) return STATUS.red;
    const diffMs = Date.now() - parsed;
    
    // Now = below warning threshold (green)
    if (diffMs <= WARNING_MS) return STATUS.green;
    // Recently = between warning and inactive thresholds (yellow)
    if (diffMs <= INACTIVE_MS) return STATUS.yellow;
    // Offline = above inactive threshold (red)
    return STATUS.red;
  };

  const getBatteryColor = (b?: number | null) => {
    if (typeof b !== 'number') return STATUS.red;
    if (b >= thresholds.batteryMedium) return STATUS.green;
    if (b >= thresholds.batteryLow) return STATUS.yellow;
    return STATUS.red;
  };

  const getCommStatus = (ts?: string | null): string => {
    if (!ts) return 'Offline';
    const parsed = Date.parse(ts);
    if (!Number.isFinite(parsed)) return 'Offline';
    const diffMs = Date.now() - parsed;
    
    // Now = below warning threshold
    if (diffMs <= WARNING_MS) return '<24h';
    // Recently = between warning and inactive thresholds
    if (diffMs <= INACTIVE_MS) return '7 Days';
    // Offline = above inactive threshold
    return 'Offline';
  };

  const getBatteryStatus = (b?: number | null): string => {
    if (typeof b !== 'number') return 'Low';
    if (b >= thresholds.batteryMedium) return 'High';
    if (b >= thresholds.batteryLow) return 'Medium';
    return 'Low';
  };

  const metricIcon: Record<string, React.ReactNode> = {
    temperature: <Thermometer size={16} color="var(--mantine-color-dimmed)" />,
    humidity: <Droplets size={16} color="var(--mantine-color-dimmed)" />,
    pressure: <Gauge size={16} color="var(--mantine-color-dimmed)" />,
  };

  // Lookup maps for sensors (normalize IDs to lowercase)
  const sensorsById = useMemo(() => new Map((directory?.sensors || []).map((s: DeviceRef) => [s.id.toLowerCase(), s])), [directory]);
  const gatewaysById = useMemo(() => new Map((directory?.gateways || []).map((g: DeviceRef) => [g.id.toLowerCase(), g])), [directory]);
  const sensorsForGateway = (gwId: string) => {
    const ids = directory?.mapping?.[gwId] || [];
    return ids
      .map((id: string) => sensorsById.get(id.toLowerCase()))
      .filter(Boolean) as { id: string; name: string }[];
  };
  const gatewayBySensor = useMemo(() => {
    const map = new Map<string, string>();
    if (directory?.mapping) {
      for (const [gid, sids] of Object.entries(directory.mapping)) {
        for (const sid of sids as string[]) map.set(sid.toLowerCase(), gid.toLowerCase());
      }
    }
    return map;
  }, [directory]);

  const formatMetricLabel = (metric: string) => {
    // Normalize incoming metric from backend, e.g. 'Humidity.general' -> 'humidity.general'
    const normalized = metric.trim().toLowerCase();
    // Try direct translation first: handles nested keys like 'humidity.general'
    const directKey = `metrics.${normalized}`;
    const translatedDirect = t(directKey);
    if (translatedDirect && translatedDirect !== directKey) return translatedDirect as string;

    // If not translated and contains dot, attempt nested path explicitly
    if (normalized.includes('.')) {
      const [parent, child] = normalized.split('.', 2);
      const nestedKey = `metrics.${parent}.${child}`;
      const translatedNested = t(nestedKey);
      if (translatedNested && translatedNested !== nestedKey) return translatedNested as string;
      // Fallback to parent label if available (e.g., 'humidity')
      const parentKey = `metrics.${parent}`;
      const translatedParent = t(parentKey);
      if (translatedParent && translatedParent !== parentKey) return translatedParent as string;
    }

    // Fallback: simple prettify
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  // (labels rendering handled inside EChartsPie via label formatter and min percentage threshold)

  return (
    <Stack gap="lg" className="summary-page">
      <Title order={2}>{t('navigation.summary') || 'Summary'}</Title>
      
      {/* Device tiles with collapsible sections, including status pies */}
      <Card withBorder p="md">
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: rem(32) }}>
            <Loader />
          </div>
        ) : error ? (
          <div style={{ padding: rem(16) }}>
            <Text c="red">{error}</Text>
          </div>
        ) : (
          <>
            <Group justify="flex-start" mb="sm" gap="sm" align="center">
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="light"
                  disabled={selectedAssetIds.size === 0}
                  onClick={() => {
                    const ids = Array.from(selectedAssetIds);
                    const gwIds = ids.filter((id) => gatewaysById.has(id));
                    const snIds = ids.filter((id) => sensorsById.has(id));
                    const params = new URLSearchParams();
                    gwIds.forEach((g) => params.append('gatewayId', g));
                    snIds.forEach((s) => params.append('sensorId', s));
                    navigate(`/data?${params.toString()}`);
                  }}
                >
                  {t('summary.actions.showInCharts') || 'In Charts anzeigen'}
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  disabled={selectedAssetIds.size === 0}
                  onClick={() => {
                    const ids = Array.from(selectedAssetIds);
                    const gwIds = ids.filter((id) => gatewaysById.has(id));
                    const snIds = ids.filter((id) => sensorsById.has(id));
                    const params = new URLSearchParams();
                    gwIds.forEach((g) => params.append('gatewayId', g));
                    snIds.forEach((s) => params.append('sensorId', s));
                    navigate(`/measurements?${params.toString()}`);
                  }}
                >
                  {t('summary.actions.showInMeasurements') || 'In Messungen anzeigen'}
                </Button>
                <Button size="xs" color="red" variant="light" disabled={selectedAssetIds.size === 0} onClick={() => {
                  setSelectedAssetIds(new Set());
                }}>
                  {t('summary.actions.clearSelection') || 'Auswahl löschen'}
                </Button>
              </Group>
            </Group>
          {byDevice.size === 0 ? (
            <Text c="dimmed">{t('summary.tiles.noDevices') || 'No devices found'}</Text>
          ) : (
            <Accordion
              multiple
              defaultValue={["status", "gateways", "sensors"]}
              variant="separated"
              styles={{
                item: { backgroundColor: 'transparent' },
                control: { backgroundColor: 'transparent' },
                content: { backgroundColor: 'transparent' },
                label: { color: 'var(--mantine-color-text)' },
                chevron: { color: 'var(--mantine-color-dimmed)' },
              }}
            >
              <Accordion.Item value="status">
                <Accordion.Control>{t('summary.status.title') || 'Statusübersicht'}</Accordion.Control>
                <Accordion.Panel style={{ backgroundColor: 'transparent' }}>
                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                    <Card withBorder p="sm">
                      <Title order={6} mb={6}>{t('summary.status.lastComm') || 'Letzte Kommunikation'}</Title>
                      {items.length > 0 ? (
                        <div style={{ height: 280 }}>
                          <EChartsPie
                            title={t('summary.status.lastComm') || 'Letzte Kommunikation'}
                            data={lastCommPie}
                            height={280}
                            showLegend
                            onSliceClick={(name, meta) => {
                              setSelectedAssetIds((prev) => {
                                const matchedIds = items
                                  .filter((d) => getCommStatus(d.lastTs) === name)
                                  .map((d) => d.id.toLowerCase());
                                const replace = !meta?.shiftKey; // default replace; Shift adds
                                if (replace) {
                                  return new Set(matchedIds);
                                }
                                const next = new Set(prev);
                                matchedIds.forEach((idLower) => {
                                  if (next.has(idLower)) next.delete(idLower); else next.add(idLower);
                                });
                                return next;
                              });
                            }}
                          />
                        </div>
                      ) : (
                        <Text c="dimmed">{t('summary.status.noData') || 'Keine Daten'}</Text>
                      )}
                    </Card>
                    <Card withBorder p="sm">
                      <Title order={6} mb={6}>{t('summary.status.battery') || 'Batteriestatus'}</Title>
                      {items.length > 0 ? (
                        <div style={{ height: 280 }}>
                          <EChartsPie
                            title={t('summary.status.battery') || 'Batteriestatus'}
                            data={batteryPie}
                            height={280}
                            showLegend
                            onSliceClick={(name, meta) => {
                              setSelectedAssetIds((prev) => {
                                const matchedIds = items
                                  .filter((d) => getBatteryStatus(d.battery) === name)
                                  .map((d) => d.id.toLowerCase());
                                const replace = !meta?.shiftKey; // default replace; Shift adds
                                if (replace) {
                                  return new Set(matchedIds);
                                }
                                const next = new Set(prev);
                                matchedIds.forEach((idLower) => {
                                  if (next.has(idLower)) next.delete(idLower); else next.add(idLower);
                                });
                                return next;
                              });
                            }}
                          />
                        </div>
                      ) : (
                        <Text c="dimmed">{t('summary.status.noData') || 'Keine Daten'}</Text>
                      )}
                    </Card>
                  </SimpleGrid>
                </Accordion.Panel>
              </Accordion.Item>

                <Accordion.Item value="gateways">
                  <Accordion.Control>{t('summary.section.gateways') || 'Gateways'}</Accordion.Control>
                  <Accordion.Panel style={{ backgroundColor: 'transparent' }}>
                    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                      {Array.from(byDevice.entries())
                        .filter(([, e]) => e.kind === 'gateway')
                        .map(([id, entry]) => {
                          const entryIdLower = entry.id.toLowerCase();
                          const isGatewayHighlighted = selectedAssetIds.has(entryIdLower);
                          return (
                            <Card
                              className="hoverable-tile"
                              key={id}
                              withBorder
                              radius="md"
                              p="md"
                              style={{
                                borderColor: isGatewayHighlighted ? 'var(--mantine-color-blue-6)' : undefined,
                                boxShadow: isGatewayHighlighted ? '0 0 0 2px var(--mantine-color-blue-6) inset' : undefined,
                                transition: 'box-shadow 150ms ease, border-color 150ms ease',
                              }}
                              onClick={() => {
                                setSelectedAssetIds((prev) => {
                                  const next = new Set(prev);
                                  // collect all sensors that belong to this gateway (lowercased ids)
                                  const sensorIdsLower = Array.from(gatewayBySensor.keys()).filter((sid) => gatewayBySensor.get(sid) === entryIdLower);
                                  const isSelected = next.has(entryIdLower);
                                  if (isSelected) {
                                    next.delete(entryIdLower);
                                    sensorIdsLower.forEach((sid) => next.delete(sid));
                                  } else {
                                    next.add(entryIdLower);
                                    sensorIdsLower.forEach((sid) => next.add(sid));
                                  }
                                  return next;
                                });
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedAssetIds((prev) => {
                                    const next = new Set(prev);
                                    const sensorIdsLower = Array.from(gatewayBySensor.keys()).filter((sid) => gatewayBySensor.get(sid) === entryIdLower);
                                    const isSelected = next.has(entryIdLower);
                                    if (isSelected) {
                                      next.delete(entryIdLower);
                                      sensorIdsLower.forEach((sid) => next.delete(sid));
                                    } else {
                                      next.add(entryIdLower);
                                      sensorIdsLower.forEach((sid) => next.add(sid));
                                    }
                                    return next;
                                  });
                                }
                              }}
                            >
                              <Stack gap={6}>
                                <Group justify="space-between" align="center">
                                  <Group gap={8} align="center">
                                    <Title order={5}>{entry.name}</Title>
                                  </Group>
                                  <Badge variant="filled" color="dark">
                                    {t('summary.tile.gateway') || 'Gateway'}
                                  </Badge>
                                </Group>
                                <Group gap={6} wrap="wrap">
                                  {sensorsForGateway(entry.id).map((sn) => {
                                    const snIdLower = sn.id.toLowerCase();
                                    const highlighted = selectedAssetIds.has(snIdLower);
                                    return (
                                      <Tooltip
                                        key={sn.id}
                                        label={`${t('navigation.sensor') || 'Sensor'}: ${sn.name}`}
                                        disabled={isMobile}
                                        withArrow
                                        openDelay={150}
                                        closeDelay={100}
                                      >
                                        <Box
                                          onMouseEnter={() => { /* no hover highlight */ }}
                                          onMouseLeave={() => { /* no hover highlight */ }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedAssetIds((prev) => {
                                              const next = new Set(prev);
                                              if (next.has(snIdLower)) next.delete(snIdLower); else next.add(snIdLower);
                                              return next;
                                            });
                                          }}
                                          onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setSelectedAssetIds((prev) => {
                                                const next = new Set(prev);
                                                if (next.has(snIdLower)) next.delete(snIdLower); else next.add(snIdLower);
                                                return next;
                                              });
                                            }
                                          }}
                                          onFocus={() => { /* noop */ }}
                                          onBlur={() => { /* noop */ }}
                                          tabIndex={0}
                                          style={{ display: 'inline-block', cursor: 'pointer' }}
                                          aria-pressed={highlighted}
                                          role="button"
                                        >
                                          <Badge
                                            variant={'filled'}
                                            color={highlighted ? 'blue' : 'dark'}
                                            style={{
                                              transition: 'transform 120ms ease, outline-color 120ms ease',
                                              transform: highlighted ? 'scale(1.04)' : 'none',
                                              outline: highlighted ? '2px solid var(--mantine-color-blue-6)' : 'none',
                                              outlineOffset: '1px',
                                            }}
                                          >
                                            {sn.name}
                                          </Badge>
                                        </Box>
                                      </Tooltip>
                                    );
                                  })}
                                  {sensorsForGateway(entry.id).length === 0 && (
                                    <Text size="sm" c="dimmed">{t('summary.tile.noMetrics') || 'Keine Metriken ausgewählt'}</Text>
                                  )}
                                </Group>
                                <Divider my={4} />
                                <Group gap={12} align="center" justify="space-between" wrap="nowrap">
                                  <Tooltip label={`${t('summary.tile.lastComm') || 'Last communication'}: ${entry.lastTs ? new Date(entry.lastTs).toLocaleTimeString() : '—'}`} disabled={isMobile} withArrow>
                                    <Group gap={6} align="center">
                                      <Clock size={16} color="var(--mantine-color-dimmed)" />
                                      <Text size="sm" fw={600} c={getCommColor(entry.lastTs)}>
                                        {entry.lastTs ? new Date(entry.lastTs).toLocaleDateString() : '—'}
                                      </Text>
                                    </Group>
                                  </Tooltip>
                                  <Tooltip label={`${t('summary.tile.battery') || 'Battery'}`} disabled={isMobile} withArrow>
                                    <Group gap={6} align="center">
                                      <Battery size={16} color="var(--mantine-color-dimmed)" />
                                      <Text size="sm" fw={600} c={getBatteryColor(typeof entry.battery === 'number' ? entry.battery : null)}>
                                        {typeof entry.battery === 'number' ? `${entry.battery}%` : '—'}
                                      </Text>
                                    </Group>
                                  </Tooltip>
                                </Group>
                              </Stack>
                            </Card>
                          );
                        })}
                    </SimpleGrid>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="sensors">
                  <Accordion.Control>{t('summary.section.sensors') || 'Sensoren'}</Accordion.Control>
                  <Accordion.Panel style={{ backgroundColor: 'transparent' }}>
                    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                      {Array.from(byDevice.entries())
                        .filter(([, e]) => e.kind === 'sensor')
                        .map(([id, entry]) => {
                          const sensorIdLower = entry.id.toLowerCase();
                          const gidLower = gatewayBySensor.get(sensorIdLower);
                          const highlighted = selectedAssetIds.has(sensorIdLower);
                          return (
                            <Card
                              className="hoverable-tile"
                              key={id}
                              withBorder
                              radius="md"
                              p="md"
                              style={{
                                borderColor: highlighted ? 'var(--mantine-color-blue-6)' : undefined,
                                boxShadow: highlighted ? '0 0 0 2px var(--mantine-color-blue-6) inset' : undefined,
                                transition: 'box-shadow 150ms ease, border-color 150ms ease',
                              }}
                            >
                              <Stack gap={6}>
                                <Group justify="space-between" align="center">
                                  <Group gap={8} align="center">
                                    <Title order={5}>{entry.name}</Title>
                                    {(() => {
                                      if (!gidLower) return null;
                                      const gwName = (gatewaysById.get(gidLower) as DeviceRef | undefined)?.name || gidLower;
                                      const gh = selectedAssetIds.has(gidLower);
                                      return (
                                        <Tooltip label={`${t('summary.tile.gateway') || 'Gateway'}: ${gwName}`} disabled={isMobile} withArrow>
                                          <ActionIcon
                                            variant={gh ? 'filled' : 'light'}
                                            color={gh ? 'blue' : 'gray'}
                                            size="sm"
                                            onClick={() => {
                                              setSelectedAssetIds((prev) => {
                                                const next = new Set(prev);
                                                const sensorIdsLower = Array.from(gatewayBySensor.keys()).filter((sid) => gatewayBySensor.get(sid) === gidLower);
                                                const isSelected = next.has(gidLower);
                                                if (isSelected) {
                                                  next.delete(gidLower);
                                                  sensorIdsLower.forEach((sid) => next.delete(sid));
                                                } else {
                                                  next.add(gidLower);
                                                  sensorIdsLower.forEach((sid) => next.add(sid));
                                                }
                                                return next;
                                              });
                                            }}
                                            aria-label="Select gateway"
                                          >
                                            <RadioTower size={14} />
                                          </ActionIcon>
                                        </Tooltip>
                                      );
                                    })()}
                                  </Group>
                                  <Badge variant="filled" color="dark">
                                    {t('summary.tile.sensor') || 'Sensor'}
                                  </Badge>
                                </Group>
                                {metricsList.length > 0 && (
                                  <Stack gap={4} mt={2}>
                                    {metricsList.slice(0, 3).map((metricKey) => {
                                      const m = entry.metrics?.[metricKey];
                                      if (!m) return null;
                                      const baseKey = metricKey.toLowerCase().split('.', 1)[0];
                                      const icon = metricIcon[baseKey] ?? null;
                                      // Use valueTypeName for humidity metrics if available, otherwise use translated label
                                      const isHumidity = metricKey.toLowerCase().startsWith('humidity');
                                      const label = (isHumidity && entry.valueTypeName) 
                                        ? entry.valueTypeName 
                                        : formatMetricLabel(metricKey);
                                      // Use valueTypeValue for humidity metrics if available, otherwise use m.unit
                                      const unit = (isHumidity && entry.valueTypeValue) 
                                        ? entry.valueTypeValue 
                                        : m.unit;
                                      return (
                                        <Group key={metricKey} gap={8} align="center">
                                          {icon}
                                          <Text size="sm" c="dimmed">
                                            {label}:{' '}
                                            <Text span inherit fw={600}>
                                              {m.avg?.toFixed ? m.avg.toFixed(1) : m.avg}
                                              {unit ? ` ${unit}` : ''}
                                            </Text>
                                          </Text>
                                        </Group>
                                      );
                                    })}
                                  </Stack>
                                )}
                                <Divider my={4} />
                                <Group gap={12} align="center" justify="space-between" wrap="nowrap">
                                  <Tooltip label={`${t('summary.tile.lastComm') || 'Last communication'}: ${entry.lastTs ? new Date(entry.lastTs).toLocaleTimeString() : '—'}`} disabled={isMobile} withArrow>
                                    <Group gap={6} align="center">
                                      <Clock size={16} color="var(--mantine-color-dimmed)" />
                                      <Text size="sm" fw={600} c={getCommColor(entry.lastTs)}>
                                        {entry.lastTs ? new Date(entry.lastTs).toLocaleDateString() : '—'}
                                      </Text>
                                    </Group>
                                  </Tooltip>
                                  <Tooltip label={`${t('summary.tile.battery') || 'Battery'}`} disabled={isMobile} withArrow>
                                    <Group gap={6} align="center">
                                      <Battery size={16} color="var(--mantine-color-dimmed)" />
                                      <Text size="sm" fw={600} c={getBatteryColor(typeof entry.battery === 'number' ? entry.battery : null)}>
                                        {typeof entry.battery === 'number' ? `${entry.battery}%` : '—'}
                                      </Text>
                                    </Group>
                                  </Tooltip>
                                </Group>
                              </Stack>
                            </Card>
                          );
                        })}
                    </SimpleGrid>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            )}
            <Group justify="flex-end" mt="md">
              <Text c="dimmed">{t('data.table.total', { count: total }) || `${total} total`}</Text>
            </Group>
          </>
        )}
      </Card>
    </Stack>
  );
};

export default SummaryPage;
